/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {toAbsoluteUrl} from '../../../helpers'
import {KTCard, KTCardBody, Earning, Select, Loading, Datepicker} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {leaveUpdate, leaveGetId} from '../../../redux/slices/staff.slice'
import {staffGetAll, basicLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  LeaveType: state.lookup.LeaveType,
  loading: state.lookup.loading,
})

const selectStaffData = (state) => ({
  isRedirect: state.staff.isRedirect,
  detail: state.staff.detail,
  loading: state.staff.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const {LeaveType, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectStaffData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(staffGetAll())
    dispatch(basicLookup('LeaveType'))
    dispatch(leaveGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    StartDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    DayCount: Yup.number()
      .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
      .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    LeaveTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    StartDate: '',
    EndDate: '',
    DayCount: '',
    LeaveTypeId: '',
    Name: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(leaveUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current) {
      const populatedValues = {
        Id: detail.id,
        StartDate: detail.startDate ? new Date(detail.startDate) : new Date(),
        EndDate: detail.endDate ? new Date(detail.endDate) : new Date(),
        DayCount: detail.dayCount || '',
        LeaveTypeId: detail.leaveTypeId || '',
        Name: detail.name || '',
      }
      
      formik.setValues(populatedValues)
      isFormPopulatedRef.current = true
    }
  }, [detail, formik])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.LEAVE'}),
      path: '/staff/leave',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/staff/leave/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.LEAVE.EDIT'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          <KTCard>
            <KTCardBody>
              {detail && (
                <form className='form' onSubmit={formik.handleSubmit} noValidate>
                  <div className='row mb-7'>
                    <div className='d-flex align-items-center mb-7'>
                      <div className='symbol symbol-50px me-5'>
                        <img
                          src={toAbsoluteUrl('/images/icons/duotune/communication/com013.svg')}
                          alt=''
                        />
                      </div>
                      <div className='flex-grow-1'>
                        <div className='text-dark fw-bold fs-6'>{detail.staffNameSurname}</div>
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'LEAVE.TYPENAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={LeaveType}
                        required
                        {...formik.getFieldProps('LeaveTypeId')}
                        onChange={(item) =>
                          formik.setFieldValue('LeaveTypeId', Number(item?.value))
                        }
                      />
                      {formik.touched.LeaveTypeId && formik.errors.LeaveTypeId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.LeaveTypeId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'EXPLANATION'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        rows={4}
                        {...formik.getFieldProps('Name')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'LEAVE.DATE.START'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        required
                        {...formik.getFieldProps('StartDate')}
                        onChange={([date]) => formik.setFieldValue('StartDate', date)}
                      />
                      {formik.touched.StartDate && formik.errors.StartDate && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.StartDate}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'LEAVE.DATE.END'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('EndDate')}
                        onChange={([date]) => formik.setFieldValue('EndDate', date)}
                      />
                      {formik.touched.EndDate && formik.errors.EndDate && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.EndDate}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'LEAVE.DAYCOUNT'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='number'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('DayCount')}
                      />
                      {formik.touched.DayCount && formik.errors.DayCount && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.DayCount}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row pt-15'>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                      <button
                        className='btn btn-light'
                        onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                      >
                        {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        data-kt-users-modal-action='submit'
                        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                      >
                        <span className='indicator-label'>
                          {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                        </span>
                        {formik.isSubmitting && (
                          <span className='indicator-progress'>
                            {intl.formatMessage({id: 'PLEASE'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </KTCardBody>
          </KTCard>
        </div>

        <div className='col-xl-3'>{detail?.staffId && <Earning StaffId={detail?.staffId} />}</div>
      </div>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
